import { useContext, useState } from 'react';
import { ViewContext, CatalogDispatchContext } from '../../store/CatalogContext';
import SortView from './SortView';
import { createSlug } from '../../utils/slugify';
import type { FilterOption, IndividualBundleOption } from '../../types/filters';
import SearchAndSuggestion from './SearchAndSuggestions';
import type { Product } from '../../types/product';

interface SearchFiltersTopbarProps {
  group: 'purchased' | 'related' | 'fullCatalog';
  prefix: string;
  filters: {
    collection: FilterOption[];
    category: FilterOption[];
    subCategory: FilterOption[];
    individualBundle?: IndividualBundleOption[];
    showBundleFilters?: boolean;
  };
  filteredProducts: Product[];
}

export default function SearchFiltersTopbar({ group, prefix, filters, filteredProducts }: SearchFiltersTopbarProps) {
  const viewState = useContext(ViewContext);
  const catalogDispatch = useContext(CatalogDispatchContext);

  const [showAllFilters] = useState({
    collection: true,
    category: true,
    subCategory: true,
  });

  const filterLimit = 10;

  // const handleShowAllFilters = (filterType: keyof typeof showAllFilters) => {
  //   setShowAllFilters((prev) => ({ ...prev, [filterType]: !prev[filterType] }));
  // };

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      value,
      checked,
      dataset: { filterType },
    } = e.target;

    type FilterType = 'collection' | 'category' | 'subCategory';

    const currentFilter = viewState[group].filters[filterType as FilterType] || [];
    let updatedFilters = [...currentFilter];

    if (checked) {
      updatedFilters.push(value);
    } else {
      updatedFilters = updatedFilters.filter((item) => item !== value);
    }

    const updatedView = {
      [group]: {
        ...viewState[group],
        filters: {
          ...viewState[group].filters,
          [filterType as FilterType]: updatedFilters,
        },
      },
    };

    catalogDispatch({
      type: 'VIEW',
      payload: {
        ...updatedView,
      },
    });

    // store filter settings in sessionStorage
    sessionStorage.setItem(
      'catalogView',
      JSON.stringify({
        ...viewState,
        ...updatedView,
      })
    );
  };

  const categoryFilters = (showAllFilters.category ? filters?.category : filters?.category?.slice(0, filterLimit))?.map(
    (item) => (
      <li className="py-1 px-3" key={item.value}>
        <div className="form-check mb-0">
          <input
            className="form-check-input"
            type="checkbox"
            value={item.value}
            id={`${prefix}-category-${createSlug(item.value)}`}
            data-filter-type="category"
            onChange={handleFilterChange}
            checked={!!viewState[group].filters.category?.includes(item.value)}
            disabled={item.matching === 0}
          />
          <label className="form-check-label d-block" htmlFor={`${prefix}-category-${createSlug(item.value)}`}>
            {item.value} <span className="text-muted small">({item.total})</span>
          </label>
        </div>
      </li>
    )
  );

  const subCategoryFilters = (
    showAllFilters.subCategory ? filters?.subCategory : filters?.subCategory?.slice(0, filterLimit)
  )?.map((item) => (
    <li className="py-1 px-3" key={item.value}>
      <div className="form-check mb-0">
        <input
          className="form-check-input"
          type="checkbox"
          value={item.value}
          id={`${prefix}-sub-category-${createSlug(item.value)}`}
          data-filter-type="subCategory"
          onChange={handleFilterChange}
          checked={!!viewState[group].filters.subCategory?.includes(item.value)}
          disabled={item.matching === 0}
        />
        <label className="form-check-label d-block" htmlFor={`${prefix}-sub-category-${createSlug(item.value)}`}>
          {item.value} <span className="text-muted small">({item.total})</span>
        </label>
      </div>
    </li>
  ));

  return (
    <div className="filter-topbar-wrap card-shadow bg-white rounded px-1 py-2">
      <div className="row gx-2 m-0">
        <div className="col-12 col-lg-9 mb-2 mb-lg-0">
          <div className="row g-2">
            <div className="col-12 col-md-6 col-lg-4 col-xl-5">
              <SearchAndSuggestion group={group} prefix={prefix} filteredProducts={filteredProducts} />
            </div>
            <div className="col-sm-6 col-md-3 col-lg-4 col-xl-3">
              <div className="dropdown filter-dropdown">
                <button
                  className="dropdown-toggle w-100 d-block text-start border rounded"
                  type="button"
                  data-bs-toggle="dropdown"
                  data-bs-auto-close="outside"
                  aria-expanded="false"
                >
                  Category
                </button>
                <ul className="dropdown-menu filter-dropdown-menu w-100">
                  {categoryFilters}

                  {/* {!showAllFilters.category && filters?.category.length > filterLimit && (
                    <li className="py-0 px-3">
                      <button
                        className="btn btn-link show-all-btn text-decoration-none p-0 mt-1"
                        type="button"
                        onClick={() => handleShowAllFilters('category')}
                      >
                        &#43; Show All <span className="text-muted small">({filters.category.length - filterLimit})</span>
                      </button>
                    </li>
                  )} */}
                </ul>
              </div>
            </div>
            <div className="col-sm-6 col-md-3 col-lg-4 col-xl-3">
              <div className="dropdown filter-dropdown">
                <button
                  className="dropdown-toggle w-100 d-block text-start border rounded"
                  type="button"
                  data-bs-toggle="dropdown"
                  data-bs-auto-close="outside"
                  aria-expanded="false"
                >
                  Sub-Category
                </button>
                <ul className="dropdown-menu filter-dropdown-menu w-100">
                  {subCategoryFilters}

                  {filters?.showBundleFilters && (
                    <>
                      <li className="py-1 px-3">
                        <div className="form-check mb-0">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            value="Individual Course"
                            id={`${prefix}-individual-products`}
                            data-filter-type="individualBundle"
                            onChange={handleFilterChange}
                            checked={!!viewState[group].filters.individualBundle?.includes('Individual Course')}
                          />
                          <label className="form-check-label d-block" htmlFor={`${prefix}-individual-products`}>
                            Individual Course
                          </label>
                        </div>
                      </li>
                      <li className="py-1 px-3">
                        <div className="form-check mb-0">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            value="Bundle/Package"
                            id={`${prefix}-bundle-products`}
                            data-filter-type="individualBundle"
                            onChange={handleFilterChange}
                            checked={!!viewState[group].filters.individualBundle?.includes('Bundle/Package')}
                          />
                          <label className="form-check-label d-block" htmlFor={`${prefix}-bundle-products`}>
                            Bundle/Package
                          </label>
                        </div>
                      </li>
                    </>
                  )}

                  {/* {!showAllFilters.subCategory && filters?.subCategory.length > filterLimit && (
                    <li className="py-0 px-3">
                      <button
                        className="btn btn-link show-all-btn text-decoration-none p-0 mt-1"
                        type="button"
                        onClick={() => handleShowAllFilters('subCategory')}
                      >
                        &#43; Show All <span className="text-muted small">({filters.subCategory.length - filterLimit})</span>
                      </button>
                    </li>
                  )} */}
                </ul>
              </div>
            </div>
          </div>
        </div>

        <SortView group={group} prefix={prefix} />
      </div>
    </div>
  );
}
